/**
 * Custom Variable Flyout Callback for iOS Safari Compatibility
 *
 * This file provides a custom implementation of Blockly's VARIABLE category callback
 * with additional null checks and error handling to prevent crashes on iOS 15.8 Safari.
 *
 * Issue: iOS 15.8 Safari has XML parsing bugs that can return null/incomplete DOM elements
 * when Blockly creates dynamic variable blocks, causing "null is not an object" errors.
 */

import * as Blockly from "blockly";

/**
 * Custom variable flyout callback with defensive null checks
 * This wraps Blockly's default VariablesDynamic.flyoutCategoryBlocks with error handling
 */
export function variablesFlyoutCallback(workspace: Blockly.Workspace): Element[] {
  const xmlList: Element[] = [];

  try {
    // Create "Create variable..." button
    const button = document.createElement("button");
    if (!button) {
      console.warn("Failed to create variable button element");
      return xmlList;
    }

    button.setAttribute("text", "%{BKY_NEW_VARIABLE}");
    button.setAttribute("callbackKey", "CREATE_VARIABLE");
    xmlList.push(button);

    // Get all variables from workspace
    const variableModelList = workspace.getVariablesOfType("");

    if (variableModelList && variableModelList.length > 0) {
      // Sort variables by name
      variableModelList.sort(Blockly.VariableModel.compareByName);

      for (let i = 0; i < variableModelList.length; i++) {
        const variable = variableModelList[i];
        if (!variable) continue;

        try {
          // Create variable getter block
          const blockText = `<block type="variables_get">
            <field name="VAR" id="${variable.getId()}">${variable.name}</field>
          </block>`;

          const block = Blockly.utils.xml.textToDom(blockText);

          // Add null check before accessing block properties - iOS Safari fix
          if (block && block.nodeType === Node.ELEMENT_NODE) {
            xmlList.push(block as Element);
          } else {
            console.warn("Failed to create variable block for:", variable.name);
          }
        } catch (error) {
          console.warn("Error creating variable block:", error);
          // Continue with other variables even if one fails
          continue;
        }
      }
    }

    // Add separator and variable setter block if there are variables
    if (variableModelList && variableModelList.length > 0) {
      try {
        const separator = document.createElement("sep");
        if (separator) {
          separator.setAttribute("gap", "24");
          xmlList.push(separator);
        }

        const setBlockText = '<block type="variables_set"></block>';
        const setBlock = Blockly.utils.xml.textToDom(setBlockText);

        if (setBlock && setBlock.nodeType === Node.ELEMENT_NODE) {
          xmlList.push(setBlock as Element);
        }
      } catch (error) {
        console.warn("Error creating variable setter:", error);
      }
    }

  } catch (error) {
    console.error("Error in variablesFlyoutCallback:", error);
    // Return what we have so far rather than crashing
  }

  return xmlList;
}

/**
 * Register the custom variable callback with Blockly
 * Call this during workspace initialization
 */
export function registerVariableCallback() {
  try {
    // Register the callback for variable creation button
    const createVariable = function(button: Blockly.FlyoutButton) {
      Blockly.Variables.createVariableButtonHandler(
        button.getTargetWorkspace(),
        undefined,
        ""
      );
    };

    // @ts-ignore - Blockly's callback registration typing
    if (Blockly.Variables && Blockly.Variables.flyoutCategory) {
      // Override the default VARIABLE callback with our safe version
      workspace => {
        const xmlList = variablesFlyoutCallback(workspace);
        return xmlList;
      };
    }

    // Register button callback
    if (typeof Blockly.FlyoutButton !== 'undefined') {
      // @ts-ignore
      Blockly.FlyoutButton.registerButtonCallback?.("CREATE_VARIABLE", createVariable);
    }
  } catch (error) {
    console.error("Failed to register variable callback:", error);
  }
}
